import { Badge } from "@/components/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip";
import { useSetExperimentRolloutDurability } from "@/http/api-hooks";
import { Experiment, ExperimentRolloutDurability } from "@/http/api-types";
import { formatUTCDateTime, getErrorMessage } from "@/lib/utils";
import { useState } from "react";
import { toast } from "sonner";
import { RolloutDurabilityDialog } from "./RolloutDurabilityDialog";
import {
  formatRolloutDurabilityAge,
  getExperimentRolloutDurabilityFreshness,
  getExperimentRolloutDurabilityOption,
  getRolloutDurabilityFreshnessBadgeVariant,
  getRolloutLifespan,
  ROLLOUT_DURABILITY_AGING_AFTER_DAYS,
  ROLLOUT_DURABILITY_PROVEN_DAYS,
} from "./experiment-rollout";

type RolloutDurabilityCellProps = {
  experiment: Experiment;
  // Injected so the badge's age is computed against one clock for the whole table
  // instead of drifting per row, and so it stays testable.
  now: Date;
};

// The durability badge on the experiments list, and the control for setting it.
//
// This is a component rather than inline JSX in the table's map() because it owns a
// per-experiment mutation hook, which cannot be called from inside a loop.
export function RolloutDurabilityCell({ experiment, now }: RolloutDurabilityCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { trigger: setDurability, isMutating } = useSetExperimentRolloutDurability(experiment.id);

  const currentOption = getExperimentRolloutDurabilityOption(experiment.rollout_durability_status);
  const stillLiveOption = getExperimentRolloutDurabilityOption(ExperimentRolloutDurability.StillLive);
  const freshness = getExperimentRolloutDurabilityFreshness(experiment, now);

  const handleOpenChange = (open: boolean) => {
    if (isMutating) {
      return;
    }
    setIsOpen(open);
  };

  // Still live is the only outcome this cell sets, and it needs nothing beyond the click:
  // no note (the API only demands one for reverted and churned) and no date
  // (rollout_durability_ended_at is omitted so the server stamps the write instant rather
  // than trusting this browser's clock).
  const confirmStillLive = async () => {
    try {
      await setDurability({ rollout_durability_status: ExperimentRolloutDurability.StillLive });
      setIsOpen(false);
      toast.success("Confirmed still live");
    } catch (error) {
      toast.error("Failed to confirm still live", { description: getErrorMessage(error) });
    }
  };

  const badge = currentOption ? (
    <Badge
      variant={
        freshness.isTracked ? getRolloutDurabilityFreshnessBadgeVariant(freshness.level) : currentOption.badgeVariant
      }
    >
      {currentOption.label}
      {freshness.isTracked && ` · ${formatRolloutDurabilityAge(freshness.ageDays)}`}
    </Badge>
  ) : (
    <Badge variant="durability_unconfirmed">Not confirmed</Badge>
  );

  // Relies on a TooltipProvider from an ancestor — the experiments list wraps its whole
  // table in one, so the hover delay stays consistent with the other cells.
  return (
    <>
      <Tooltip>
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                disabled={isMutating}
                aria-label={
                  currentOption
                    ? `Rollout durability: ${currentOption.label}. Change it.`
                    : "Record the rollout durability"
                }
              >
                {badge}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>

          {/* Suppressed while the popover is open so the two do not stack on the badge.

            TableCell sets whitespace-nowrap and TooltipContent is not portalled, so the
            tooltip inherits it and its text runs off the side under the content's
            overflow-hidden. whitespace-normal restores wrapping. */}
          {!isOpen && (
            <TooltipContent className="max-w-xs whitespace-normal p-2 text-xs">
              <RolloutDurabilityTooltipBody experiment={experiment} />
            </TooltipContent>
          )}

          <PopoverContent align="start" className="w-72 p-2">
            <div className="flex flex-col gap-1">
              <button
                type="button"
                disabled={isMutating}
                onClick={() => void confirmStillLive()}
                className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-accent disabled:opacity-50"
              >
                <span className="text-sm font-medium">
                  {stillLiveOption?.label}
                  {experiment.rollout_durability_status === ExperimentRolloutDurability.StillLive && " (re-confirm)"}
                </span>
                <span className="text-xs text-muted-foreground">{stillLiveOption?.description}</span>
              </button>

              {/* Reverted and churned need an end date and a note explaining what went
                wrong, so they open the same form the experiment page uses rather than a
                cut-down copy of it — the note is written with the rich Editor either way,
                which is what lets it link the revert PR, the incident, or the client
                comms. Still live stays a single click above. */}
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setIsDialogOpen(true);
                }}
                className="mt-1 w-full rounded-md border-t border-border px-2 pb-1 pt-2 text-left text-xs text-muted-foreground hover:text-foreground"
              >
                Reverted or client churned? Record it →
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </Tooltip>

      {/* Outside the Tooltip root: the dialog outlives the hover that opened it. */}
      <RolloutDurabilityDialog experiment={experiment} open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </>
  );
}

// Always paired with the rollout date, because the span between the two is the number
// that matters: how long the change actually survived, which is what the 90-day
// durability gate measures a revert against.
//
// The second date depends on the outcome. Reverted and churned each name their own event
// and drop "Checked on" — when it happened is what counts, not when somebody typed it in.
// Still live has no end date at all: its ended_at is the write instant repeating
// recorded_at, so it shows how old the last check is instead.
//
// The durability note is deliberately absent. It is markdown authored in the Editor, so a
// tooltip would show its source — literal asterisks, link syntax, mention markup — and an
// arbitrarily long note at that. It is rendered properly on the experiment page.
function RolloutDurabilityTooltipBody({ experiment }: { experiment: Experiment }) {
  if (!experiment.rollout_durability_status) {
    return <p>Nobody has confirmed this rollout is still live.</p>;
  }

  const endedAtLabel = getExperimentRolloutDurabilityOption(experiment.rollout_durability_status)?.endedAtLabel;
  const lifespan = getRolloutLifespan(experiment);

  return (
    <dl className="space-y-1">
      {experiment.rollout_at && (
        <div>
          <dt className="font-medium">Rolled out</dt>
          <dd>{formatUTCDateTime(experiment.rollout_at)}</dd>
        </div>
      )}
      {endedAtLabel
        ? experiment.rollout_durability_ended_at && (
            <div>
              <dt className="font-medium">{endedAtLabel}</dt>
              <dd>{formatUTCDateTime(experiment.rollout_durability_ended_at)}</dd>
              {/* The span the durability gate actually judges. Spelling it out saves the
                  reader subtracting two timestamps to find out whether this one counts. */}
              {lifespan && (
                <dd className={lifespan.provenDurable ? "text-muted-foreground" : "font-medium"}>
                  {`Lived ${lifespan.days}d`}
                  {!lifespan.provenDurable && ` — under the ${ROLLOUT_DURABILITY_PROVEN_DAYS}d durability bar`}
                </dd>
              )}
            </div>
          )
        : experiment.rollout_durability_recorded_at && (
            <div>
              <dt className="font-medium">Checked on</dt>
              <dd>{formatUTCDateTime(experiment.rollout_durability_recorded_at)}</dd>
            </div>
          )}
      {!endedAtLabel && (
        <p className="pt-1 text-muted-foreground">
          {`Confirmations older than ${ROLLOUT_DURABILITY_AGING_AFTER_DAYS} days are flagged for a re-check.`}
        </p>
      )}
    </dl>
  );
}
